/**
 * Standardized character export utility.
 * All export locations should use this module for consistent output.
 */
import type { CharacterDraft } from '../stores/characterCreationStore';
import type { CustomEffect, CustomCheatSheetTip } from '../stores/playSessionStore';

/**
 * Current schema version for exports.
 * Increment when making breaking changes to the export format.
 */
export const SCHEMA_VERSION = '1.0';

/**
 * Runtime state for play sessions - preserves play progress across export/import.
 * This is a subset of PlaySession focused on essential state that would be lost.
 */
export interface RuntimeState {
  /** HP tracking */
  hitPoints: {
    current: number;
    max: number;
    temporary: number;
  };

  /** Death saving throws */
  deathSaves: {
    successes: number;
    failures: number;
  };

  /** Spell slots by level (1-9) and 'pact' for warlock */
  spellSlots: Record<string, { max: number; used: number }>;

  /** Hit dice by die type (e.g., 'd8', 'd10') */
  hitDice: Record<string, { max: number; used: number }>;

  /** Class resources like Ki Points, Sorcery Points, etc. */
  classResources: Record<string, { max: number; used: number }>;

  /** Active conditions */
  conditions: string[];

  /** Exhaustion level (0-6) */
  exhaustionLevel: number;

  /** Current concentration spell */
  concentrationSpell: string | null;

  /** Session notes (optional - useful for story continuity) */
  sessionNotes?: string;

  /** Bardic Inspiration active */
  bardicInspiration?: boolean;

  /** DM Inspiration active */
  dmInspiration?: boolean;

  /** Custom temporary effects */
  customEffects?: CustomEffect[];

  /** Cheat sheet state */
  cheatSheet?: {
    dismissedTipIds: string[];
    customTips: CustomCheatSheetTip[];
    tipOrder: string[];
    combatNotes: string;
  };
}

/**
 * The standardized export format for character data.
 */
export interface CharacterExportData {
  /** Schema version for migration support */
  schemaVersion: string;
  /** ISO 8601 timestamp when exported */
  exportedAt: string;
  /** The character draft data */
  draft: CharacterDraft;
  /** Optional runtime state (HP, spell slots, etc.) */
  runtimeState?: RuntimeState;
}

/**
 * Create a standardized export data object from a character draft.
 *
 * @param draft - The character draft to export
 * @param runtimeState - Optional runtime state (HP, spell slots, etc.)
 * @returns The standardized export data object
 */
export function exportCharacter(
  draft: CharacterDraft,
  runtimeState?: RuntimeState
): CharacterExportData {
  const exportData: CharacterExportData = {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    draft,
  };

  // Only include runtimeState if provided
  if (runtimeState) {
    exportData.runtimeState = runtimeState;
  }

  return exportData;
}

/**
 * Generate a safe filename for the character export.
 *
 * @param characterName - The character's name
 * @returns A sanitized filename with .json extension
 */
export function generateExportFilename(characterName: string): string {
  // Use character name or default
  const baseName = characterName.trim() || 'character';

  // Sanitize: remove invalid filename characters, limit length
  const sanitized = baseName
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid chars
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .substring(0, 50); // Limit length

  return `${sanitized}.json`;
}

/**
 * Trigger a browser download of a JSON string with the given filename.
 */
export function downloadJsonFile(json: string, filename: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export character to a JSON file and trigger download.
 */
export function downloadCharacterJson(
  draft: CharacterDraft,
  runtimeState?: RuntimeState
): void {
  const exportData = exportCharacter(draft, runtimeState);
  const json = JSON.stringify(exportData, null, 2);
  downloadJsonFile(json, generateExportFilename(draft.name));
}
